import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

/** Every Kenney model the game uses (CC0; see public/models/LICENSE.md), by kit. */
const MANIFEST: Record<string, string[]> = {
  "car-kit": ["race-future", "sedan-sports", "hatchback-sports", "race", "suv-luxury", "taxi", "police", "delivery", "garbage-truck", "firetruck", "truck", "box", "cone", "wheel-truck"],
  "city-kit-roads": ["construction-barrier", "construction-fence", "construction-cone", "construction-light", "traffic-light", "light-curved", "light-curved-double", "bridge-pillar-wide", "road-bridge", "dumpster", "sign-highway-wide", "sign-highway-detailed", "road-sign-warning", "electricity-pole"],
  "city-kit-suburban": ["planter", "tree-large", "tree-small", "fence-low", "building-type-a", "building-type-c", "building-type-f", "building-type-h", "building-type-k", "building-type-m", "building-type-p", "building-type-s"],
  "city-kit-commercial": ["building-a", "building-b", "building-c", "building-d", "building-e", "building-f", "building-g", "building-h", "building-i", "building-j", "building-skyscraper-a", "building-skyscraper-b", "building-skyscraper-c", "building-skyscraper-d", "building-skyscraper-e", "low-detail-building-a", "low-detail-building-b", "low-detail-building-c", "low-detail-building-d", "low-detail-building-e", "low-detail-building-wide-a", "low-detail-building-wide-b", "detail-awning"],
  "city-kit-industrial": ["shipping-container-a", "shipping-container-b", "shipping-container-c", "detail-tank", "water-tower", "chimney-large", "building-a", "building-c", "building-f"]
};

export interface ModelInfo {
  scene: THREE.Group;
  /** bounding box of the model as authored */
  size: THREE.Vector3;
  min: THREE.Vector3;
}

/**
 * Loads the models once, gives each kit one shared MeshStandardMaterial per material name (Kenney paints every model
 * of a kit from one colour map, so a kit costs a single texture on the GPU instead of one per file), and hands out
 * clones that share geometry and materials. Matte paint is Lambert (cheap per pixel), the "-specular" parts (windows)
 * a glossy PBR material that reflects the environment map.
 */
export class Assets {
  private models = new Map<string, ModelInfo>();
  private materialCache = new Map<string, THREE.Material>();

  static keys(): string[] {
    return Object.entries(MANIFEST).flatMap(([kit, names]) => names.map((n) => `${kit}/${n}`));
  }

  async load(onProgress?: (done: number, total: number) => void): Promise<void> {
    const loader = new GLTFLoader();
    const keys = Assets.keys();
    let done = 0;
    await Promise.all(keys.map(async (key) => {
      const gltf = await loader.loadAsync(`${import.meta.env.BASE_URL}models/${key}.glb`);
      const scene = gltf.scene;
      const kit = key.split("/")[0];
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.material = Array.isArray(mesh.material) ? mesh.material.map((m) => this.standard(m, kit)) : this.standard(mesh.material, kit);
        mesh.castShadow = false;
        mesh.receiveShadow = false;
      });
      const box = new THREE.Box3().setFromObject(scene);
      this.models.set(key, { scene, size: box.getSize(new THREE.Vector3()), min: box.min.clone() });
      done += 1;
      onProgress?.(done, keys.length);
    }));
  }

  has(key: string): boolean {
    return this.models.has(key);
  }

  info(key: string): ModelInfo {
    const model = this.models.get(key);
    if (!model) throw new Error(`model not loaded: ${key}`);
    return model;
  }

  /** A clone sharing geometry and materials. */
  clone(key: string): THREE.Group {
    return this.info(key).scene.clone(true);
  }

  /**
   * A clone fitted to a box in metres: `size` per axis (undefined keeps the uniform scale of the axes that are set),
   * sitting on y = 0 and centred on x/z. `rotateY` turns the model first (in quarter turns).
   */
  fit(key: string, size: { x?: number; y?: number; z?: number }, rotateY = 0): THREE.Group {
    const info = this.info(key);
    const inner = info.scene.clone(true);
    inner.rotation.y = rotateY * Math.PI / 2;
    const authored = rotateY % 2 === 0 ? info.size.clone() : new THREE.Vector3(info.size.z, info.size.y, info.size.x);
    const sx = size.x !== undefined ? size.x / authored.x : undefined;
    const sy = size.y !== undefined ? size.y / authored.y : undefined;
    const sz = size.z !== undefined ? size.z / authored.z : undefined;
    const uniform = sx ?? sy ?? sz ?? 1;
    const scale = new THREE.Vector3(sx ?? uniform, sy ?? uniform, sz ?? uniform);
    const holder = new THREE.Group();
    holder.add(inner);
    holder.scale.copy(scale);
    // centre on x/z, put the bottom on y = 0 (in the holder's unscaled space)
    const box = new THREE.Box3().setFromObject(inner);
    const center = box.getCenter(new THREE.Vector3());
    inner.position.set(-center.x, -box.min.y, -center.z);
    const outer = new THREE.Group();
    outer.add(holder);
    return outer;
  }

  private standard(material: THREE.Material, kit: string): THREE.Material {
    const source = material as THREE.MeshStandardMaterial;
    const key = `${kit}:${source.name}:${source.map ? "map" : "flat"}:${source.color?.getHexString() ?? ""}:${source.transparent}`;
    const cached = this.materialCache.get(key);
    if (cached) return cached;
    // matte paint is Lambert (no environment sampling: the city is most of the screen and the game is fill-rate bound);
    // only the "-specular" parts (windows) keep a glossy PBR material
    const glossy = source.name.includes("specular");
    const params = { name: `${kit}/${source.name}`, map: source.map ?? null, color: source.color ?? new THREE.Color(0xffffff), transparent: source.transparent, opacity: source.opacity };
    const made: THREE.MeshStandardMaterial | THREE.MeshLambertMaterial = glossy
      ? new THREE.MeshStandardMaterial({ ...params, roughness: 0.35, metalness: 0, envMapIntensity: 1 })
      : new THREE.MeshLambertMaterial(params);
    if (made.map) { made.map.colorSpace = THREE.SRGBColorSpace; made.map.anisotropy = 4; }
    this.materialCache.set(key, made);
    return made;
  }
}
